import { Color } from '@persona-id/design-tokens';
import { SuggestionProps } from '@tiptap/suggestion';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { createPortal } from 'react-dom';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import AvailabilityCircle from 'dashboard/components-lib/AvailabilityCircle';
import Menu, { MenuItem } from 'design-system/components/Menu/Menu';
import IconUserProfilePicture from 'images/icons/user-profile-picture.svg';

const StyledMenuItem = styled(MenuItem)<{ isSelected: boolean }>`
  width: 210px;
  ${({ isSelected }) =>
    isSelected &&
    ` background-color: ${Color.CorePurple100};
      color: ${Color.CorePurple700};`};
`;

const ProfilePictureImage = styled('img')`
  border-radius: ${BORDER_RADIUSES.circle};
  object-fit: cover;
`;

const StyledIconUserProfilePicture = styled(IconUserProfilePicture)`
  border-radius: ${BORDER_RADIUSES.circle};
`;

const PROFILE_PICTURE_WIDTH = 20;
const PROFILE_PICTURE_HEIGHT = 20;

interface MentionListProps extends SuggestionProps {}

export const MentionList = forwardRef(
  ({ items, command, decorationNode }: MentionListProps, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const selectItem = (index: number) => {
      if (items.length === 0) {
        return;
      }
      const displayName = items[index].displayName;
      const userId = items[index].userId;

      command({ id: userId, label: displayName });
    };

    const upHandler = () => {
      setSelectedIndex((selectedIndex + items.length - 1) % items.length);
    };

    const downHandler = () => {
      setSelectedIndex((selectedIndex + 1) % items.length);
    };

    const enterHandler = () => {
      selectItem(selectedIndex);
    };

    useEffect(() => setSelectedIndex(0), [items]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          upHandler();
          return true;
        }

        if (event.key === 'ArrowDown') {
          downHandler();
          return true;
        }

        if (event.key === 'Enter') {
          enterHandler();
          return true;
        }

        return false;
      },
    }));

    const boundingRect = decorationNode?.getBoundingClientRect();

    return createPortal(
      items.length && boundingRect ? (
        <Menu
          variant='light'
          containerProps={{
            style: {
              position: 'absolute',
              top: boundingRect.y,
              left: boundingRect.x,
              transform: 'translateY(-100%)',
            },
          }}
        >
          {items.map((item, index) => (
            <StyledMenuItem
              isSelected={index === selectedIndex}
              id='MenuList__item'
              key={index}
              onClick={() => selectItem(index)}
              suffixIcon={
                item.availability && <AvailabilityCircle availability={item.availability} />
              }
              prefixIcon={
                item.profilePictureUrl ? (
                  <ProfilePictureImage
                    height={PROFILE_PICTURE_HEIGHT}
                    width={PROFILE_PICTURE_WIDTH}
                    src={item.profilePictureUrl as string}
                  />
                ) : (
                  <StyledIconUserProfilePicture
                    height={PROFILE_PICTURE_HEIGHT}
                    width={PROFILE_PICTURE_WIDTH}
                  />
                )
              }
            >
              {item.displayName}
            </StyledMenuItem>
          ))}
        </Menu>
      ) : null,
      document.body,
    );
  },
);

MentionList.displayName = 'MentionList';
